import { sharedSheet } from "../styles/sharedStyles.js";
import componentStylesText from "./LoadingBlockComponent.css?inline";

const componentSheet = new CSSStyleSheet();
componentSheet.replaceSync(componentStylesText);

/**
 * LoadingBlockComponent — A component for rendering a loading indicator bubble in the chat.
 *
 * It renders the following structure:
 * <div class="chat-message__bubble">
 *   <div class="chat-message__typing" aria-hidden="true">
 *     <span class="chat-message__dot"></span>
 *     <span class="chat-message__dot"></span>
 *     <span class="chat-message__dot"></span>
 *   </div>
 * </div>
 */
export class LoadingBlockComponent extends HTMLElement {
    static get observedAttributes(): string[] {
        return ["role"];
    }

    /**
     * Returns the primary text content for accessibility aggregation.
     */
    get accessibilityText(): string {
        return "Loading...";
    }

    /**
     * Called when the element is first connected to the document's DOM.
     * Initializes the internal structure if not already present.
     */
    connectedCallback(): void {
        if (!this.shadowRoot) {
            const shadow = this.attachShadow({ mode: "open" });
            shadow.adoptedStyleSheets = [sharedSheet, componentSheet];
            this.render();
        }
    }

    /**
     * Creates the internal DOM structure of the component.
     * Uses standard CSS classes for styling compatibility.
     * Builds the DOM with standard DOM APIs as requested.
     */
    private render(): void {
        const bubble = document.createElement("div");
        bubble.className = "chat-message__bubble";

        // Create the typing animation container (hidden from screen readers)
        const typingContainer = document.createElement("div");
        typingContainer.className = "chat-message__typing";
        typingContainer.setAttribute("aria-hidden", "true");

        // Create three dots for the animation
        for (let i = 0; i < 3; i++) {
            const dot = document.createElement("span");
            dot.className = "chat-message__dot";
            typingContainer.appendChild(dot);
        }

        bubble.appendChild(typingContainer);
        this.shadowRoot?.appendChild(bubble);
    }
}

/**
 * Register the custom element if it hasn't been registered yet.
 */
if (!customElements.get("jt-loading-block")) {
    customElements.define("jt-loading-block", LoadingBlockComponent);
}
